import Phaser from "phaser";
import {
  listDropfrontLevels,
  setDropfrontLevel,
} from "../data/dropfront-catalog";
import { t } from "../data/strings";
import { SceneKey, assertTransition } from "../flow/game-flow";
import { sharedInput } from "../input/input-controller";
import { getUiLayout } from "../ui/layout";
import { clearOverlay } from "../ui/screen-overlay";
import { markActiveScene } from "../ui/screen-state";

/**
 * Dropfront level select — Title ↔ Select ↔ Play.
 */
export class DropfrontSelectScene extends Phaser.Scene {
  private disposed = false;

  constructor() {
    super(SceneKey.DropfrontSelect);
  }

  create(): void {
    this.disposed = false;
    markActiveScene(SceneKey.DropfrontSelect);
    sharedInput.attach();
    clearOverlay();

    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor("#0e1628");
    this.add.rectangle(width / 2, height / 2, width, height, 0x0e1628).setDepth(-1);
    this.add
      .text(width / 2, height * 0.1, t("df.select.title"), {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "28px",
        color: "#ffd27a",
      })
      .setOrigin(0.5);
    this.add
      .text(width / 2, height * 0.155, t("df.select.subtitle"), {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "13px",
        color: "#9aa3c7",
      })
      .setOrigin(0.5);

    this.buildMenuDom();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  update(): void {
    if (this.disposed) return;
    const pressed = sharedInput.consumePressed();
    if (pressed.cancel) this.backToTitle();
  }

  private buildMenuDom(): void {
    const ui = getUiLayout();
    ui.sync();
    ui.clearShell();
    const frame = ui.frame;
    frame.style.display = "flex";
    frame.style.flexDirection = "column";
    frame.style.alignItems = "center";
    frame.style.justifyContent = "center";
    frame.style.pointerEvents = "auto";
    ui.overlay.style.pointerEvents = "auto";

    const panel = document.createElement("div");
    panel.className = "menu-panel";
    const levels = listDropfrontLevels();
    const rows = levels
      .map((lv) => {
        const label = this.escapeHtml(t(lv.nameKey));
        return `
          <div class="menu-row" data-row="${lv.id}">
            <button type="button" class="menu-btn" data-level="${lv.id}">
              ${lv.index}. ${label}
            </button>
          </div>`;
      })
      .join("");
    panel.innerHTML = `
      <section class="menu-section menu-section-play">
        <h2 class="menu-section-title">${this.escapeHtml(t("df.select.levels"))}</h2>
        ${rows}
        <div class="menu-row">
          <button type="button" class="menu-btn" data-back>${this.escapeHtml(t("df.select.back"))}</button>
        </div>
      </section>`;
    frame.appendChild(panel);

    for (const lv of levels) {
      panel
        .querySelector(`[data-level="${lv.id}"]`)
        ?.addEventListener("click", () => this.startLevel(lv.id));
    }
    panel
      .querySelector("[data-back]")
      ?.addEventListener("click", () => this.backToTitle());
  }

  private startLevel(id: string): void {
    if (this.disposed) return;
    setDropfrontLevel(id);
    assertTransition(SceneKey.DropfrontSelect, SceneKey.Dropfront);
    this.teardown();
    this.scene.start(SceneKey.Dropfront);
  }

  private backToTitle(): void {
    if (this.disposed) return;
    assertTransition(SceneKey.DropfrontSelect, SceneKey.Title);
    this.teardown();
    this.scene.start(SceneKey.Title);
  }

  private escapeHtml(s: string): string {
    return s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  private teardown(): void {
    this.disposed = true;
    clearOverlay();
  }
}
